import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  LayoutGrid, 
  RefreshCw, 
  Printer, 
  ArrowRight,
  CheckCircle2,
  Clock,
  FileText,
  AlertTriangle,
  AlertCircle,
  ScrollText,
  HeartHandshake,
  Check,
  Loader2,
  Users
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useLifely } from '../context/LifelyContext';
import { toast } from 'sonner';

const Dashboard = () => {
  const { 
    user, 
    isAuthenticated,
    getSummaryStats, 
    continuityConfig, 
    performCheckIn,
    fetchMaps,
    fetchContacts,
    fetchWillStatus,
    fetchContinuity,
    loading,
  } = useLifely();
  
  const [justCheckedIn, setJustCheckedIn] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);

  // Fetch data when authenticated
  useEffect(() => {
    const loadData = async () => {
      if (isAuthenticated && !dataLoaded) {
        try {
          await Promise.all([
            fetchMaps(),
            fetchContacts(),
            fetchWillStatus(),
            fetchContinuity(),
          ]);
          setDataLoaded(true);
        } catch (err) {
          console.error('Failed to load dashboard data:', err);
        }
      }
    };
    loadData();
  }, [isAuthenticated, dataLoaded, fetchMaps, fetchContacts, fetchWillStatus, fetchContinuity]);

  const stats = getSummaryStats();

  const handleCheckIn = async () => {
    try {
      const result = await performCheckIn();
      setJustCheckedIn(true);
      const interval = continuityConfig?.checkInInterval || 2;
      const unit = continuityConfig?.checkInUnit || 'weeks';
      if (result?.nextCheckIn) {
        toast.success(`Checked in! Next check-in extended to ${new Date(result.nextCheckIn).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}`);
      } else {
        toast.success(`Checked in! Next check-in in ${interval} ${unit}.`);
      }
      setTimeout(() => setJustCheckedIn(false), 3000);
    } catch (err) {
      toast.error('Check-in failed. Please try again.');
    }
  };

  // Helper to get holder label
  const getHolderLabel = (value) => {
    const labels = {
      'lawyer': 'Lawyer / Law Firm',
      'trustee': 'Trustee',
      'personal': 'Stored Personally',
      'other': 'Other'
    };
    return labels[value] || '—';
  };

  // Show loading state while fetching initial data
  const isLoading = loading.maps || loading.contacts || loading.willStatus || loading.continuity;

  // Get user display name
  const displayName = user?.firstName || user?.fullName?.split(' ')[0] || 'there';

  return (
    <div className="space-y-8 animate-fade-in" data-testid="dashboard-page">
      {/* Welcome Section */}
      <section className="space-y-2">
        <h1 className="text-3xl sm:text-4xl font-heading font-semibold text-foreground">
          Welcome, <span data-ms-member="first-name">{displayName}</span>
        </h1>
        <p className="text-muted-foreground text-base max-w-2xl">
          Your calm space for mapping what matters. Take your time—there&apos;s no rush.
        </p>
      </section>

      {/* Loading Indicator */}
      {isLoading && (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
          <span className="ml-2 text-muted-foreground">Loading your data...</span>
        </div>
      )}

      {/* Summary Cards */}
      {!isLoading && (
        <>
          <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="summary-cards">
            {/* Maps Status Card */}
            <Card className="card-hover border border-border" data-testid="maps-status-card">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base font-medium font-body">
                  <LayoutGrid className="w-4 h-4 text-primary" />
                  Maps Status
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2 text-muted-foreground">
                      <span className="w-2 h-2 rounded-full bg-primary"></span>
                      Configured
                    </span>
                    <span className="font-medium">{stats.configuredMaps}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2 text-muted-foreground">
                      <span className="w-2 h-2 rounded-full bg-muted-foreground/40"></span>
                      Not Configured
                    </span>
                    <span className="font-medium">{stats.notConfiguredMaps}</span>
                  </div>
                </div>
                <div className="pt-2 border-t border-border space-y-1">
                  <p className="text-xs text-muted-foreground">
                    {stats.totalEntries} entries across {stats.totalMaps} maps
                  </p>
                  {(stats.criticalCount > 0 || stats.highCount > 0) && (
                    <div className="flex items-center gap-2 flex-wrap">
                      {stats.criticalCount > 0 && (
                        <Badge variant="outline" className="text-xs bg-destructive/10 text-destructive border-destructive/20">
                          <AlertTriangle className="w-3 h-3 mr-1" />
                          {stats.criticalCount} Critical
                        </Badge>
                      )}
                      {stats.highCount > 0 && (
                        <Badge variant="outline" className="text-xs bg-accent/20 text-accent border-accent/30">
                          <AlertCircle className="w-3 h-3 mr-1" />
                          {stats.highCount} High
                        </Badge>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Will Status Card */}
            <Card className={`card-hover border-2 ${stats.willExists === 'yes' ? 'border-green-500/30 bg-green-500/5' : 'border-border'}`} data-testid="will-status-card">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base font-medium font-body">
                  <ScrollText className={`w-4 h-4 ${stats.willExists === 'yes' ? 'text-green-600' : 'text-primary'}`} />
                  Will Status
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    {stats.willExists === 'yes' ? (
                      <>
                        <CheckCircle2 className="w-4 h-4 text-green-600" />
                        <span className="text-sm font-medium text-green-700 dark:text-green-400">Will exists</span>
                      </>
                    ) : stats.willExists === 'no' ? (
                      <>
                        <AlertTriangle className="w-4 h-4 text-destructive" />
                        <span className="text-sm text-destructive">No will</span>
                      </>
                    ) : (
                      <>
                        <Clock className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">Not specified</span>
                      </>
                    )}
                  </div>
                  {stats.willExists === 'yes' && stats.willHeldWith && (
                    <p className="text-sm text-muted-foreground">
                      Held with: {getHolderLabel(stats.willHeldWith)}
                    </p>
                  )}
                  {stats.willExists === 'yes' && stats.willFirmName && (
                    <p className="text-sm text-muted-foreground truncate">
                      {stats.willFirmName}
                    </p>
                  )}
                  {/* Reminder status */}
                  <div className="flex items-center gap-2 pt-1">
                    {stats.willReminderDate ? (
                      <>
                        <CheckCircle2 className="w-3 h-3 text-primary" />
                        <span className="text-xs text-muted-foreground">Review reminder configured</span>
                      </>
                    ) : (
                      <>
                        <Clock className="w-3 h-3 text-muted-foreground" />
                        <span className="text-xs text-muted-foreground">Review reminder not set</span>
                      </>
                    )}
                  </div>
                </div>
                <Link to="/will-status" className="block">
                  <Button variant="ghost" size="sm" className="w-full justify-start text-xs h-8 px-2">
                    <ArrowRight className="w-3 h-3 mr-1" />
                    View details
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Continuity Status Card */}
            <Card className={`card-hover border-2 ${stats.continuityConfigured ? 'border-green-500/30 bg-green-500/5' : 'border-blue-500/30 bg-blue-500/5'}`} data-testid="continuity-status-card">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base font-medium font-body">
                  <RefreshCw className={`w-4 h-4 ${stats.continuityConfigured ? 'text-green-600' : 'text-blue-600'}`} />
                  Continuity Status
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    {stats.continuityConfigured ? (
                      <>
                        <CheckCircle2 className="w-4 h-4 text-green-600" />
                        <span className="text-sm font-medium text-green-700 dark:text-green-400">Active</span>
                      </>
                    ) : (
                      <>
                        <Clock className="w-4 h-4 text-blue-600" />
                        <span className="text-sm text-blue-700 dark:text-blue-400">Not configured</span>
                      </>
                    )}
                  </div>
                  {stats.continuityConfigured && (
                    <>
                      <p className="text-sm text-muted-foreground">
                        {stats.nomineesCount} nominee{stats.nomineesCount !== 1 ? 's' : ''} added
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Check-in every {continuityConfig?.checkInInterval || continuityConfig?.checkInFrequency || 3} {continuityConfig?.checkInUnit || 'months'}
                      </p>
                    </>
                  )}
                </div>
                {!stats.continuityConfigured && (
                  <p className="text-xs text-muted-foreground">
                    Set up check-ins and nominees.
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Activity Card */}
            <Card className="card-hover border border-border" data-testid="last-updated-card">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base font-medium font-body">
                  <FileText className="w-4 h-4 text-primary" />
                  Recent Activity
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {stats.recentActivity.length > 0 ? (
                  <div className="space-y-3">
                    {stats.recentActivity.slice(0, 3).map((activity, idx) => (
                      <div key={activity.mapId || activity.id || idx} className="text-xs space-y-0.5">
                        <div className="flex items-center justify-between">
                          <span className="text-foreground font-medium flex items-center gap-1">
                            {activity.type === 'map' && activity.mapName?.split(' ')[0]}
                            {activity.type === 'contact' && (
                              <>
                                <Users className="w-3 h-3" />
                                Contact
                              </>
                            )}
                            {activity.type === 'will' && (
                              <>
                                <ScrollText className="w-3 h-3" />
                                Formal
                              </>
                            )}
                          </span>
                          <span className="text-muted-foreground/60">
                            {activity.lastUpdated?.split('T')[0] || activity.lastUpdated}
                          </span>
                        </div>
                        <p className="text-muted-foreground truncate">
                          {activity.type === 'map' && activity.lastEntryLabel}
                          {activity.type === 'contact' && `${activity.name} (${activity.contactCount} total)`}
                          {activity.type === 'will' && `Will status: ${activity.willExists || 'Updated'}`}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    No recent activity
                  </p>
                )}
                {stats.recentActivity.length > 3 && (
                  <p className="text-xs text-muted-foreground pt-1 border-t border-border">
                    +{stats.recentActivity.length - 3} more recent updates
                  </p>
                )}
              </CardContent>
            </Card>
          </section>

          {/* Check-in Section (only shown when continuity is configured) */}
          {stats.continuityConfigured && (
            <section data-testid="check-in-section">
              <Card className="border-2 border-primary/20 bg-primary/5" data-testid="check-in-card">
                <CardContent className="flex flex-col sm:flex-row items-center justify-between gap-4 p-6">
                  <div className="flex items-center gap-4 text-center sm:text-left">
                    <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
                      <HeartHandshake className="w-6 h-6 text-primary" />
                    </div>
                    <div className="space-y-1">
                      <h3 className="font-medium text-foreground">Stay connected</h3>
                      {continuityConfig?.nextCheckIn ? (
                        <p className="text-sm text-muted-foreground">
                          Next check-in: <span className="font-medium text-foreground">{new Date(continuityConfig.nextCheckIn).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}</span>
                        </p>
                      ) : continuityConfig?.lastCheckIn ? (
                        <p className="text-sm text-muted-foreground">
                          Last check-in: {new Date(continuityConfig.lastCheckIn).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </p>
                      ) : (
                        <p className="text-sm text-muted-foreground">
                          No check-ins yet
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground">
                        Extend your next check-in by {continuityConfig?.checkInInterval || continuityConfig?.checkInFrequency || 3} {continuityConfig?.checkInUnit || 'months'}. 
                        <Link to="/continuity/setup" className="text-primary hover:underline ml-1">Change in settings</Link>
                      </p>
                    </div>
                  </div>
                  <Button 
                    onClick={handleCheckIn}
                    disabled={justCheckedIn}
                    className="shrink-0 min-w-[140px]"
                    data-testid="check-in-btn"
                  >
                    {justCheckedIn ? (
                      <>
                        <Check className="w-4 h-4 mr-2" />
                        Checked In!
                      </>
                    ) : (
                      <>
                        <CheckCircle2 className="w-4 h-4 mr-2" />
                        Check In
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            </section>
          )}

          {/* Primary Actions */}
          <section className="space-y-4" data-testid="primary-actions">
            <h2 className="text-lg font-heading font-medium text-foreground">
              Quick Actions
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Link to="/maps" data-testid="view-maps-action">
                <Card className="card-hover border border-border cursor-pointer group h-full">
                  <CardContent className="flex items-center justify-between p-6">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <LayoutGrid className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium text-sm">View Maps</p>
                        <p className="text-xs text-muted-foreground">Review and update your maps</p>
                      </div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-transform duration-200" />
                  </CardContent>
                </Card>
              </Link>

              <Link to="/continuity" data-testid="update-continuity-action">
                <Card className="card-hover border border-border cursor-pointer group h-full">
                  <CardContent className="flex items-center justify-between p-6">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-accent/20 flex items-center justify-center">
                        <RefreshCw className="w-5 h-5 text-accent" />
                      </div>
                      <div>
                        <p className="font-medium text-sm">Update Continuity</p>
                        <p className="text-xs text-muted-foreground">Configure check-ins and nominees</p>
                      </div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-accent group-hover:translate-x-1 transition-transform duration-200" />
                  </CardContent>
                </Card>
              </Link>

              <Link to="/print-export" data-testid="create-bundle-action">
                <Card className="card-hover border border-border cursor-pointer group h-full">
                  <CardContent className="flex items-center justify-between p-6">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
                        <Printer className="w-5 h-5 text-muted-foreground" />
                      </div>
                      <div>
                        <p className="font-medium text-sm">Create Printable Bundle</p>
                        <p className="text-xs text-muted-foreground">Export your maps for printing</p>
                      </div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-foreground group-hover:translate-x-1 transition-transform duration-200" />
                  </CardContent>
                </Card>
              </Link>
            </div>
          </section>
        </>
      )}

      {/* Helpful Note */}
      <section className="mt-12">
        <div className="bg-muted/50 rounded-xl p-6 border border-border">
          <p className="text-sm text-muted-foreground leading-relaxed">
            <span className="font-medium text-foreground">Remember:</span> Lifely is designed to help you map what matters without storing sensitive information. 
            Never enter passwords, private keys, seed phrases, or credentials. Focus on what exists, where it&apos;s managed, and your intentions.
          </p>
        </div>
      </section>
    </div>
  );
};

export default Dashboard;
